/* eslint-disable no-param-reassign */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';

import updateAllPadding from '../updateAllPadding';
import ElementPadding from '../../../../componentLibrary/ElementPadding';
import Button from '../../../../componentLibrary/Button';
import Input from '../../../../componentLibrary/Input';
import ButtonGroup from '../../../../componentLibrary/ButtonGroup/ButtonGroup';
import HorizontalRule from '../../../../componentLibrary/HorizontalRule';
import InputValidator from '../../../../componentLibrary/InputValidator';
import paddingChange from '../../paddingChange';
import SocialSelect from './SocialSelect';
import ActiveIcon from './ActiveIcon';
import { elementContentType } from '../../../../types';
import * as columnItemActions from '../../../../actions/columnItem';
import * as Styled from './styles';

const ValidatedSize = InputValidator(Input, { min: 0, max: 100 });

class SocialSetting extends PureComponent {
    state = {
        blockIndex: null,
        columnIdx: null,
        elmIdx: null,
        content: null,
        animate: [],
        activeIcons: [],
        contentValues: {
            align: 'center',
            iconSize: 32,
            iconVersionColor: true,
            icons: [],
            paddingTop: 0,
            paddingRight: 0,
            paddingLeft: 0,
            paddingBottom: 0,
        },
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { selectedElementData } = nextProps;
        const { blockIndex, columnIdx, elmIdx, content } = selectedElementData;
        const { align, iconSize, iconVersionColor, icons, paddingTop, paddingRight, paddingBottom, paddingLeft } =
            content;
        const contentValues = {
            align,
            iconSize,
            iconVersionColor,
            icons,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        };
        const propsChanged =
            elmIdx !== prevState.elmIdx || JSON.stringify(contentValues) !== JSON.stringify(prevState.contentValues);
        const activeIcons = icons.filter((icon) => icon.active);
        if (propsChanged) {
            return { ...prevState, elmIdx, blockIndex, columnIdx, content, contentValues, activeIcons };
        }
        return { ...prevState };
    }

    componentWillUnmount() {
        // when the user clicks on a canvasBlock, the selected element is removed from the state due to a dispatch on removeAllSelectedElements
        // turns out that onBlur never ends up running in this use case, not validating the size
        // to make up for this use case we call onBlur here in order to run the validation for the input size > 15
        this._sizeInput.props.onBlur();
    }

    updateIconsOrder = (icon, targetIcon, direction) => {
        const {
            contentValues: { icons },
        } = this.state;
        const movedIcon = { ...icon, src: { ...icon.src } };
        const newIndex = icons.findIndex((cbIcon) => cbIcon.title === targetIcon.title);
        const newIcons = icons.filter((cbIcon) => cbIcon.title !== movedIcon.title);
        newIcons.splice(newIndex, 0, movedIcon);
        const oppositeDirection = direction === 'top' ? 'bottom' : 'top';
        this.setState({
            animate: [
                { title: icon.title, direction },
                { title: targetIcon.title, direction: oppositeDirection },
            ],
        });
        this._updateContent('icons', newIcons);
        setTimeout(() => {
            this.setState({ animate: [] });
        }, 200);
    };

    addIcon = (icon) => {
        const {
            contentValues: { icons },
        } = this.state;
        const newIcons = icons.filter((cbIcon) => cbIcon.title !== icon.title);
        newIcons.push(icon);
        this._updateContent('icons', newIcons);
        setTimeout(() => {
            const newIcon = document.getElementById(icon.title);
            newIcon && newIcon.scrollIntoView({ behavior: 'smooth' });
        }, 250);
    };

    _updateContent = (key, value) => {
        const { blockIndex, columnIdx, elmIdx, content, contentValues, activeIcons } = this.state;
        const { dispatch } = this.props;
        let newContentValues;
        let newIcons = [...activeIcons];
        if (key === 'updateAllPadding') {
            newContentValues = updateAllPadding(contentValues, value);
        } else if (key === 'icons') {
            newIcons = value.filter((cbIcon) => cbIcon.active);
            newContentValues = { ...contentValues, [key]: value };
        } else {
            newContentValues = { ...contentValues, [key]: value };
        }
        const newContent = { ...content, ...newContentValues };
        dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
            activeIcons: newIcons,
        }));
    };

    _renderAlignButton = (align, direction) => (
        <Button
            type="simple"
            icon={`align-${direction}`}
            iconSize={15}
            iconPadding={7}
            ariaPressed={align === direction}
            width={28}
            marginBottom={1}
            color={align === direction ? '#0084d1' : '#727273'}
            label={`Align ${direction[0].toUpperCase() + direction.slice(1).toLowerCase()}`}
            ariaLabel={`Align social icons to ${direction[0].toUpperCase() + direction.slice(1).toLowerCase()}`}
            onClick={() => {
                if (align === direction) {
                    return;
                }
                this._updateContent('align', direction);
            }}
        />
    );

    render() {
        const { contentValues, animate, activeIcons } = this.state;
        const { align, iconSize, iconVersionColor, icons, paddingTop, paddingRight, paddingBottom, paddingLeft } =
            contentValues;
        const { hasFullStylingAccess } = this.props;
        const alignmentButtons = ['left', 'center', 'right'];

        return (
            <div>
                <Styled.Row>
                    <HorizontalRule />
                    <ElementPadding
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}
                        paddingLeft={paddingLeft}
                        paddingRight={paddingRight}
                        elementType="social"
                        onChange={(event) => {
                            const payload = paddingChange({
                                event,
                                paddingBottom,
                                paddingLeft,
                                paddingRight,
                                paddingTop,
                            });
                            if (!payload) {
                                return;
                            }
                            const { key, value = 0 } = payload;
                            this._updateContent(key, value);
                        }}
                    />
                    <HorizontalRule />
                    <Styled.Label>icon style</Styled.Label>
                    <ButtonGroup
                        options={['Default', 'Gray']}
                        selected={iconVersionColor ? 0 : 1}
                        onClick={() => {
                            this._updateContent('iconVersionColor', !iconVersionColor);
                        }}
                    />
                    <Styled.InputWrapper>
                        <div>
                            <Styled.SmallLabel>Size</Styled.SmallLabel>
                            <ValidatedSize
                                ref={(input) => {
                                    this._sizeInput = input;
                                }}
                                type="number-small"
                                width={86}
                                max={100}
                                initialValue={+iconSize || ''}
                                label="icon size"
                                icon="fullscreen"
                                onChange={(event) => {
                                    const value = +event.target.value;
                                    if (value === iconSize) {
                                        return;
                                    }
                                    this._updateContent('iconSize', value);
                                }}
                                onBlur={() => {
                                    if (iconSize < 15) {
                                        this._updateContent('iconSize', 15);
                                    }
                                }}
                            />
                        </div>
                        <div style={{ width: '48%' }}>
                            <Styled.SmallLabel margin="0 0 0 7px">Alignment</Styled.SmallLabel>
                            <div>{alignmentButtons.map((direction) => this._renderAlignButton(align, direction))}</div>
                        </div>
                    </Styled.InputWrapper>
                    {hasFullStylingAccess && (
                        <>
                            <Styled.Label>social icon</Styled.Label>
                            <SocialSelect
                                icons={icons}
                                colorIcons={iconVersionColor}
                                onChange={({ value: title }) => {
                                    const icon = icons.find((cbIcon) => cbIcon.title === title);
                                    this.addIcon({ ...icon, active: !icon.active });
                                }}
                            />
                        </>
                    )}
                    <HorizontalRule />
                    <div style={{ overflowX: 'hidden', overflowY: 'scroll', height: 400 }}>
                        {activeIcons.map((icon) => {
                            const { title, href, active } = icon;
                            const first = activeIcons[0].title === title;
                            const last = active && activeIcons[activeIcons.length - 1].title === title;
                            const iconSrc = icon.src[iconVersionColor ? 'color' : 'bw'];
                            const animationDetails = animate.filter((an) => an.title === title);
                            const isAnimated = animationDetails.length > 0;
                            const direction = animationDetails[0] && animationDetails[0].direction;
                            return active ? (
                                <ActiveIcon
                                    id={title}
                                    key={`${_uniqueId(title)}-active`}
                                    title={title}
                                    isAnimated={isAnimated}
                                    direction={direction}
                                    activeIcons={activeIcons}
                                    updateIconsOrder={this.updateIconsOrder}
                                    first={first}
                                    last={last}
                                    icon={icon}
                                    icons={icons}
                                    updateContent={this._updateContent}
                                    href={href}
                                    iconSrc={iconSrc}
                                    hasFullStylingAccess={hasFullStylingAccess}
                                />
                            ) : null;
                        })}
                    </div>
                </Styled.Row>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    // Undefined has been purposfully set to true for accounts without this feature.
    hasFullStylingAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_STYLING_ACCESS', true),
});

SocialSetting.propTypes = {
    dispatch: PropTypes.func.isRequired,
    hasFullStylingAccess: PropTypes.bool.isRequired,
    selectedElementData: PropTypes.shape({
        blockIndex: PropTypes.string.isRequired,
        columnIdx: PropTypes.string.isRequired,
        elmIdx: PropTypes.string.isRequired,
        content: elementContentType.isRequired,
    }).isRequired,
};

export default connect(mapStateToProps)(SocialSetting);
